import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  Bell,
  Shield,
  Eye,
  EyeOff,
  Check,
  Camera,
  Trash2,
  AlertCircle,
  Edit,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type SettingsTab = 'account' | 'limits' | 'password';

interface ProfileData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  cpfCnpj: string;
  documentType: 'CPF' | 'CNPJ';
  type: string;
  status: string;
  avatarUrl?: string;
  address?: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

interface LimitData {
  type: string;
  label: string;
  total: number;
  used: number;
  available: number;
  usagePercentage: number;
}

export function AccountSettings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>('account');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Edit form state
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  
  // Email change state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailChangeSuccess, setEmailChangeSuccess] = useState(false);
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Fetch profile data
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['banking', 'settings', 'profile'],
    queryFn: async () => {
      const response = await api.get('/banking/settings/profile');
      return response.data as ProfileData;
    },
  });

  // Fetch limits
  const { data: limitsData, isLoading: limitsLoading } = useQuery({
    queryKey: ['banking', 'settings', 'limits'],
    queryFn: async () => {
      const response = await api.get('/banking/settings/profile/limits');
      return response.data as { limits: LimitData[] };
    },
    enabled: activeTab === 'limits',
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: { name?: string; phone?: string }) => {
      const response = await api.put('/banking/settings/profile', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banking', 'settings', 'profile'] });
      setIsEditing(false);
    },
  });

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await api.post('/banking/settings/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banking', 'settings', 'profile'] });
    },
  });

  // Remove avatar mutation
  const removeAvatarMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/banking/settings/profile/avatar');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banking', 'settings', 'profile'] });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const response = await api.post('/banking/settings/security/change-password', data);
      return response.data;
    },
    onSuccess: () => {
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
  });

  // Change email mutation
  const changeEmailMutation = useMutation({
    mutationFn: async (data: { newEmail: string; currentPassword: string }) => {
      const response = await api.post('/banking/settings/security/change-email', data);
      return response.data;
    },
    onSuccess: () => {
      setShowEmailModal(false);
      setNewEmail('');
      setEmailPassword('');
      setEmailChangeSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'settings'] });
      setTimeout(() => setEmailChangeSuccess(false), 4000);
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDocument = (doc: string, type: 'CPF' | 'CNPJ') => {
    if (type === 'CPF') {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const tabs = [
    { id: 'account' as SettingsTab, label: 'Conta', icon: <User size={18} /> },
    { id: 'limits' as SettingsTab, label: 'Limites', icon: <Shield size={18} /> },
    { id: 'password' as SettingsTab, label: 'Senha', icon: <Lock size={18} /> },
  ];

  const handleStartEdit = () => {
    if (profileData) {
      setEditName(profileData.name);
      setEditPhone(profileData.phone || '');
      setIsEditing(true);
    }
  };

  const handleSaveProfile = () => {
    updateProfileMutation.mutate({
      name: editName,
      phone: editPhone || undefined,
    });
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAvatarMutation.mutate(file);
    }
  };

  const handleChangePassword = () => {
    if (newPassword !== confirmPassword) {
      return;
    }
    changePasswordMutation.mutate({
      currentPassword,
      newPassword,
    });
  };

  const isPasswordValid = 
    newPassword.length >= 8 && 
    /[A-Z]/.test(newPassword) && 
    /[a-z]/.test(newPassword) && 
    /[0-9]/.test(newPassword) && 
    /[^A-Za-z0-9\s]/.test(newPassword) &&
    newPassword === confirmPassword;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Configurações da Conta</h1>
          <p className="text-surface-400">Gerencie suas informações e preferências</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/banking/security')}>
            <Shield size={18} className="mr-2" />
            Segurança
          </Button>
          <Button variant="secondary" onClick={() => navigate('/banking/notifications')}>
            <Bell size={18} className="mr-2" />
            Notificações
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Account Tab */}
      {activeTab === 'account' && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">Dados da Conta</h2>
              {!isEditing && (
                <Button variant="secondary" size="sm" onClick={handleStartEdit}>
                  Editar
                </Button>
              )}
            </div>

            {profileLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-3 w-20 bg-surface-700 rounded mb-2" />
                      <div className="h-4 w-40 bg-surface-700 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : isEditing ? (
              <div className="space-y-4">
                {/* Avatar Edit */}
                <div className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50">
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center overflow-hidden">
                      {profileData?.avatarUrl ? (
                        <img 
                          src={profileData.avatarUrl} 
                          alt="Avatar" 
                          className="w-full h-full object-cover"
                          style={{ minWidth: '64px', minHeight: '64px' }}
                        />
                      ) : (
                        <User size={32} className="text-primary-400" />
                      )}
                    </div>
                    <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-600">
                      <Camera size={14} className="text-white" />
                      <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                    </label>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">Foto de Perfil</p>
                    <p className="text-xs text-surface-400">JPG, PNG ou WebP. Máx 2MB.</p>
                  </div>
                  {profileData?.avatarUrl && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeAvatarMutation.mutate()}
                      disabled={removeAvatarMutation.isPending}
                    >
                      <Trash2 size={16} className="text-red-400" />
                    </Button>
                  )}
                </div>

                <Input
                  label="Nome"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Seu nome completo"
                />

                <Input
                  label="Telefone"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  placeholder="11999999999"
                />

                <div className="flex gap-3 pt-4">
                  <Button variant="secondary" className="flex-1" onClick={() => setIsEditing(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    variant="primary" 
                    className="flex-1" 
                    onClick={handleSaveProfile}
                    disabled={updateProfileMutation.isPending}
                  >
                    {updateProfileMutation.isPending ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Avatar Display */}
                <div className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50">
                  <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {profileData?.avatarUrl ? (
                      <img 
                        src={profileData.avatarUrl} 
                        alt="Avatar" 
                        className="w-full h-full object-cover"
                        style={{ minWidth: '64px', minHeight: '64px' }}
                      />
                    ) : (
                      <User size={32} className="text-primary-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-lg font-medium text-white truncate">{profileData?.name}</p>
                    <p className="text-sm text-surface-400">{profileData?.type}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 cursor-pointer hover:bg-surface-800 transition-colors" onClick={() => { setNewEmail(profileData?.email || ''); setShowEmailModal(true); }}>
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <Mail size={20} className="text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-surface-400">E-mail</p>
                    <p className="text-sm text-white">{profileData?.email}</p>
                  </div>
                  <Edit size={16} className="text-surface-500" />
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50">
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <Phone size={20} className="text-primary-400" />
                  </div>
                  <div>
                    <p className="text-xs text-surface-400">Telefone</p>
                    <p className="text-sm text-white">{profileData?.phone || 'Não informado'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50">
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <Shield size={20} className="text-primary-400" />
                  </div>
                  <div>
                    <p className="text-xs text-surface-400">{profileData?.documentType}</p>
                    <p className="text-sm text-white">
                      {profileData?.cpfCnpj ? formatDocument(profileData.cpfCnpj, profileData.documentType) : '-'}
                    </p>
                  </div>
                </div>

                {profileData?.address && (
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50">
                    <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <MapPin size={20} className="text-primary-400" />
                    </div>
                    <div>
                      <p className="text-xs text-surface-400">Endereço</p>
                      <p className="text-sm text-white">
                        {profileData.address.street}, {profileData.address.number}
                        {profileData.address.complement && ` - ${profileData.address.complement}`}
                        <br />
                        {profileData.address.neighborhood}, {profileData.address.city} - {profileData.address.state}
                        <br />
                        CEP: {profileData.address.zipCode}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Limits Tab */}
      {activeTab === 'limits' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Limites de Operação</h2>

            {limitsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="h-4 w-32 bg-surface-700 rounded mb-3" />
                    <div className="h-2 w-full bg-surface-700 rounded mb-2" />
                    <div className="h-3 w-24 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : limitsData?.limits?.length ? (
              <div className="space-y-4">
                {limitsData.limits.map((limit) => (
                  <div key={limit.type} className="p-4 rounded-lg bg-surface-800/50">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-medium text-white">{limit.label}</p>
                      <p className="text-xs text-surface-400">
                        {formatCurrency(limit.used)} / {formatCurrency(limit.total)}
                      </p>
                    </div>
                    <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          limit.usagePercentage > 80
                            ? 'bg-red-500'
                            : limit.usagePercentage > 50
                            ? 'bg-yellow-500'
                            : 'bg-primary-500'
                        }`}
                        style={{ width: `${Math.min(limit.usagePercentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-surface-400 mt-2">
                      Disponível: {formatCurrency(limit.available)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum limite configurado</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Alterar Senha</h2>
            <p className="text-surface-400 mb-6">
              Mantenha sua conta segura alterando sua senha periodicamente.
            </p>

            <Button variant="primary" onClick={() => setShowPasswordModal(true)}>
              <Lock size={18} className="mr-2" />
              Alterar Senha
            </Button>
          </div>
        </Card>
      )}

      {/* Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Alterar Senha"
      >
        <div className="space-y-4">
          {changePasswordMutation.isError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
              <AlertCircle size={16} className="text-red-400" />
              <span className="text-sm text-red-300">Senha atual incorreta</span>
            </div>
          )}

          <div className="relative">
            <Input
              label="Senha Atual"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Digite sua senha atual"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-9 text-surface-400 hover:text-white"
            >
              {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="relative">
            <Input
              label="Nova Senha"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Digite a nova senha"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-9 text-surface-400 hover:text-white"
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <Input
            label="Confirmar Nova Senha"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirme a nova senha"
          />

          {newPassword && (
            <div className="space-y-2 p-3 rounded-lg bg-surface-800">
              <p className="text-xs text-surface-400 mb-2">Requisitos da senha:</p>
              <PasswordRequirement met={newPassword.length >= 8} text="Mínimo de 8 caracteres" />
              <PasswordRequirement met={/[A-Z]/.test(newPassword)} text="Uma letra maiúscula" />
              <PasswordRequirement met={/[a-z]/.test(newPassword)} text="Uma letra minúscula" />
              <PasswordRequirement met={/[0-9]/.test(newPassword)} text="Um número" />
              <PasswordRequirement met={/[^A-Za-z0-9\s]/.test(newPassword)} text="Um caractere especial" />
              <PasswordRequirement met={newPassword === confirmPassword && confirmPassword.length > 0} text="Senhas coincidem" />
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowPasswordModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleChangePassword}
              disabled={!isPasswordValid || !currentPassword || changePasswordMutation.isPending}
            >
              {changePasswordMutation.isPending ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Email Change Modal */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="Alterar E-mail"
      >
        <div className="space-y-4">
          <p className="text-sm text-surface-400">
            Para alterar seu e-mail, informe o novo endereço e confirme com sua senha atual.
          </p>

          <Input
            label="Novo E-mail"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="novo@email.com"
          />

          <Input
            label="Senha Atual"
            type="password"
            value={emailPassword}
            onChange={(e) => setEmailPassword(e.target.value)}
            placeholder="Confirme sua senha"
          />

          {changeEmailMutation.isError && (
            <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {(changeEmailMutation.error as any)?.response?.data?.message || 'Erro ao alterar e-mail'}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button variant="secondary" className="flex-1" onClick={() => setShowEmailModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => changeEmailMutation.mutate({ newEmail, currentPassword: emailPassword })}
              disabled={!newEmail || !emailPassword || changeEmailMutation.isPending || newEmail === profileData?.email}
            >
              {changeEmailMutation.isPending ? 'Alterando...' : 'Alterar E-mail'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Email Change Success */}
      {emailChangeSuccess && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-green-500/90 backdrop-blur-sm text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 max-w-sm">
            <Check size={24} />
            <div>
              <p className="font-semibold">E-mail alterado com sucesso</p>
              <p className="text-sm text-green-100">Use o novo e-mail no próximo login</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function PasswordRequirement({ met, text }: { met: boolean; text: string }) {
  return (
    <div className="flex items-center gap-2">
      <Check size={14} className={met ? 'text-green-400' : 'text-surface-500'} />
      <span className={`text-xs ${met ? 'text-green-400' : 'text-surface-400'}`}>{text}</span>
    </div>
  );
}

export default AccountSettings;
